import React, { useEffect, useState } from 'react';
import { Card, Typography, Tag, Space, Spin, Button, Divider, Empty, Badge, Tooltip } from 'antd';
import {
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  SettingOutlined,
  ScheduleOutlined,
  DollarOutlined,
  PlusOutlined,
  CloseOutlined,
  SafetyCertificateOutlined,
  ToolOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { chatService } from '../../services/chatService';

const { Title, Text } = Typography;

interface Customer360PanelProps {
  userId: number | null;
  onClose?: () => void;
}

export const Customer360Panel: React.FC<Customer360PanelProps> = ({ userId, onClose }) => {
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!userId) {
      setData(null);
      return;
    }

    const load360Data = async () => {
      setLoading(true);
      try {
        const res = await chatService.getCustomer360Data(userId);
        setData(res);
      } catch (err) {
        console.error('Erreur chargement Fiche Client 360°:', err);
      } finally {
        setLoading(false);
      }
    };

    load360Data();
  }, [userId]);

  if (!userId) return null;

  const user = data?.user;
  const profile = data?.customerProfile;
  const customerName = profile
    ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
    : user
    ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
    : 'Client';

  return (
    <Card
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 16,
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        border: '1px solid #e2e8f0'
      }}
      bodyStyle={{ padding: 16, flex: 1, overflowY: 'auto' }}
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <UserOutlined style={{ color: '#0a543d' }} />
            <span style={{ fontWeight: 700, fontSize: 15 }}>Fiche Client 360°</span>
          </Space>
          {onClose && (
            <Button type="text" icon={<CloseOutlined />} onClick={onClose} />
          )}
        </div>
      }
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" tip="Chargement profil 360°..." />
        </div>
      ) : !data ? (
        <Empty description="Aucune donnée client trouvée" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* IDENTITÉ & CONTACT */}
          <div style={{ backgroundColor: '#f8fafc', padding: 12, borderRadius: 12, border: '1px solid #e2e8f0' }}>
            <Title level={5} style={{ margin: 0, color: '#0a543d' }}>
              {customerName}
            </Title>
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
              {user?.phone && (
                <div>
                  <PhoneOutlined style={{ marginRight: 6, color: '#16a34a' }} />
                  <a href={`tel:${user.phone.replace(/\s+/g, '')}`} style={{ color: '#0a543d', fontWeight: 600 }}>
                    {user.phone}
                  </a>
                </div>
              )}
              {user?.email && (
                <div>
                  <MailOutlined style={{ marginRight: 6, color: '#2563eb' }} />
                  <Text type="secondary">{user.email}</Text>
                </div>
              )}
            </div>
          </div>

          {/* ACTION RAPIDE INTERVENTION */}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{ backgroundColor: '#0a543d', borderColor: '#0a543d', borderRadius: 8, fontWeight: 600 }}
            onClick={() => navigate('/interventions')}
            block
          >
            Créer une intervention
          </Button>

          <Divider style={{ margin: '8px 0' }} />

          {/* ABONNEMENTS ET CONTRATS */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Text strong style={{ fontSize: 13, color: '#334155' }}>
                <SafetyCertificateOutlined style={{ color: '#d97706', marginRight: 6 }} />
                Abonnements & Contrats
              </Text>
              <Badge count={data.subscriptions?.length || 0} style={{ backgroundColor: '#0a543d' }} />
            </div>

            {(!data.subscriptions || data.subscriptions.length === 0) ? (
              <Text type="secondary" style={{ fontSize: 12 }}>Aucun abonnement souscrit</Text>
            ) : (
              data.subscriptions.map((sub: any) => {
                const offerName = sub.offer?.name || sub.offer_snapshot?.name || sub.offer_snapshot?.offer_name || sub.equipment_description || 'Abonnement Maintenance';
                const visitsPerYear = sub.offer?.included_visits_per_year || sub.offer_snapshot?.included_visits_per_year || sub.visits_total || 1;
                const maxEquipments = sub.offer?.max_equipments || sub.offer_snapshot?.max_equipments || sub.equipment_count || 1;
                const statusColor = sub.status === 'active' ? 'green' : sub.status === 'completed' ? 'blue' : 'orange';

                return (
                  <div key={sub.id} style={{ padding: 10, borderRadius: 8, backgroundColor: '#fffbeb', border: '1px solid #fef3c7', marginBottom: 6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text strong style={{ fontSize: 13, color: '#b45309' }}>{offerName}</Text>
                      <Tag color={statusColor}>{sub.status || 'Actif'}</Tag>
                    </div>
                    <div style={{ fontSize: 11, color: '#78350f', marginTop: 4 }}>
                      Visites incluses: {visitsPerYear} / an • Équipements max: {maxEquipments}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <Divider style={{ margin: '8px 0' }} />

          {/* ÉQUIPEMENTS ENREGISTRÉS */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Text strong style={{ fontSize: 13, color: '#334155' }}>
                <ToolOutlined style={{ color: '#2563eb', marginRight: 6 }} />
                Équipements ({data.equipments?.length || 0})
              </Text>
            </div>

            {(!data.equipments || data.equipments.length === 0) ? (
              <Text type="secondary" style={{ fontSize: 12 }}>Aucun équipement enregistré</Text>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {data.equipments.slice(0, 4).map((eq: any) => (
                  <div key={eq.id} style={{ padding: 8, borderRadius: 8, backgroundColor: '#f1f5f9', fontSize: 12 }}>
                    <Text strong style={{ display: 'block' }}>{eq.brand || 'Marque N/A'} - {eq.type || 'Climatiseur'}</Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>Emplacement: {eq.location || 'N/A'} {eq.model ? `(${eq.model})` : ''}</Text>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Divider style={{ margin: '8px 0' }} />

          {/* INTERVENTIONS RÉCENTES */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Text strong style={{ fontSize: 13, color: '#334155' }}>
                <ScheduleOutlined style={{ color: '#16a34a', marginRight: 6 }} />
                Dernières Interventions
              </Text>
            </div>

            {(!data.interventions || data.interventions.length === 0) ? (
              <Text type="secondary" style={{ fontSize: 12 }}>Aucune intervention trouvée</Text>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {data.interventions.map((int: any) => (
                  <div key={int.id} style={{ padding: 8, borderRadius: 8, backgroundColor: '#ffffff', border: '1px solid #e2e8f0', fontSize: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text strong>{int.type || 'Intervention'}</Text>
                      <Tag color={int.status === 'completed' ? 'green' : int.status === 'in_progress' ? 'blue' : 'orange'}>
                        {int.status}
                      </Tag>
                    </div>
                    {int.technician && (
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                        Tech: {int.technician.first_name} {int.technician.last_name}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* IMPAYÉS / DEVIS EN ATTENTE */}
          {data.unpaidQuotes && data.unpaidQuotes.length > 0 && (
            <>
              <Divider style={{ margin: '8px 0' }} />
              <div style={{ backgroundColor: '#fef2f2', padding: 10, borderRadius: 8, border: '1px solid #fecaca' }}>
                <Text strong style={{ color: '#dc2626', fontSize: 13 }}>
                  <DollarOutlined style={{ marginRight: 6 }} />
                  Devis / Impayés en attente ({data.unpaidQuotes.length})
                </Text>
                <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {data.unpaidQuotes.map((q: any) => (
                    <div key={q.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#991b1b' }}>
                      <span>#{q.quoteNumber || q.id}</span>
                      <strong>{q.total ? `${q.total} FCFA` : 'En attente'}</strong>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
};
